<script>
import {
  getInquiryDetail,
  postInquiryReply,
  putInquiryAnswer,
  deleteInquiryAnswer,
} from '@/api/inquiry.js'
import { getAdminInfo } from '@/utils/token.js'

export default {
  name: 'InquiryDetailPage',
  data() {
    return {
      loading: false,
      board: null,
      comments: [],
      attachments: [],
      replyText: '',
      sending: false,
      adminInfo: {},
      editingCode: null,
      editingText: '',
    }
  },
  computed: {
    boardCode() {
      return this.$route.params.boardCode
    },
  },
  mounted() {
    this.adminInfo = getAdminInfo()
    this.load()
  },
  async beforeRouteUpdate(to, from, next) {
    next()
    await this.$nextTick()
    if (to.params.boardCode) {
      await this.load(to.params.boardCode)
    }
  },
  methods: {
    async load(boardCode) {
      const bc = boardCode || this.boardCode
      this.loading = true
      try {
        const res = await getInquiryDetail(bc)
        if (res.data?.code === 0 && res.data.data) {
          this.board = res.data.data.board || null
          this.comments = res.data.data.comments || []
          this.attachments = res.data.data.attachments || []
        } else {
          this.board = null
          this.comments = []
          this.attachments = []
        }
      } catch (e) {
        console.error('[InquiryDetailPage]', e)
      } finally {
        this.loading = false
      }
    },
    formatDt(val) {
      if (!val) return '-'
      return String(val).substring(0, 19).replace('T', ' ')
    },
    formatFileSize(size) {
      if (!size) return '0 B'
      if (size < 1024) return `${size} B`
      if (size < 1024 * 1024) return `${Math.round(size / 1024)} KB`
      return `${(size / 1024 / 1024).toFixed(1)} MB`
    },
    typeLabel(type) {
      return {
        PRODUCT_PRICE: '가격문의',
        PRODUCT: '상품',
        ORDER: '주문',
        PAYMENT: '결제',
        DELIVERY: '배송',
        SIGNUP: '회원가입',
        ETC: '기타',
      }[type] || type || '-'
    },
    statusLabel(status) {
      return {
        OPEN: '문의중',
        ANSWERED: '답변완료',
        CLOSED: '종료',
      }[status] || status || '-'
    },
    canEditAnswer(c) {
      if (!c || c.boardCommentType !== 'ANSWER') return false
      if (this.adminInfo.adminType === 'HQ') return true
      return c.branchAdminCode && c.branchAdminCode === this.adminInfo.adminCode
    },
    async sendReply() {
      const t = this.replyText.trim()
      if (!t) return
      this.sending = true
      try {
        const res = await postInquiryReply(this.boardCode, t)
        if (res.data?.code === 0) {
          this.replyText = ''
          await this.load()
        }
      } catch (e) {
        console.error('[InquiryDetailPage] sendReply', e)
      } finally {
        this.sending = false
      }
    },
    startEdit(c) {
      this.editingCode = c.boardCommentCode
      this.editingText = c.boardCommentText || ''
    },
    cancelEdit() {
      this.editingCode = null
      this.editingText = ''
    },
    async saveEdit() {
      if (!this.editingCode) return
      const t = this.editingText.trim()
      if (!t) return
      this.sending = true
      try {
        const res = await putInquiryAnswer(this.editingCode, t)
        if (res.data?.code === 0) {
          this.cancelEdit()
          await this.load()
        }
      } catch (e) {
        console.error('[InquiryDetailPage] saveEdit', e)
      } finally {
        this.sending = false
      }
    },
    async removeAnswer(c) {
      if (!window.confirm('답변을 삭제할까요?')) return
      this.sending = true
      try {
        const res = await deleteInquiryAnswer(c.boardCommentCode)
        if (res.data?.code === 0) await this.load()
      } catch (e) {
        console.error('[InquiryDetailPage] removeAnswer', e)
      } finally {
        this.sending = false
      }
    },
    goList() {
      this.$router.push('/inquiries')
    },
    goMember() {
      const u = this.board?.userCode
      if (u) this.$router.push(`/members/${u}`)
    },
    openProductDetail() {
      const productCode = this.board?.productCode
      if (!productCode) return
      const route = this.$router.resolve(`/products/${encodeURIComponent(productCode)}`)
      window.open(route.href, '_blank', 'noopener,noreferrer')
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-2 mb-4">
      <div>
        <div class="text-h5 font-weight-bold">1:1 문의 상세</div>
        <div v-if="board" class="text-body-2 text-grey">
          <a href="#" class="text-primary text-decoration-none" @click.prevent="goMember">
            {{ board.ownerUserName || board.ownerUserEmail || board.userCode || '회원' }}
          </a>
          <span class="ml-2">· {{ typeLabel(board.inquiryType) }}</span>
          <span class="ml-2">· {{ statusLabel(board.inquiryStatus) }}</span>
        </div>
      </div>
      <v-btn variant="text" @click="goList">목록</v-btn>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <template v-if="board">
      <v-card rounded="lg" elevation="1" class="mb-4 pa-4">
        <div class="d-flex flex-wrap align-center justify-space-between gap-2 mb-3">
          <div class="text-subtitle-1 font-weight-bold">{{ board.boardTitle || '(제목 없음)' }}</div>
          <v-chip size="small" color="primary" variant="tonal">{{ typeLabel(board.inquiryType) }}</v-chip>
        </div>
        <div v-if="board.productName" class="inquiry-linked-product mb-3 rounded-lg pa-3 text-body-2">
          관련 상품:
          <button
            type="button"
            class="inquiry-product-link"
            :disabled="!board.productCode"
            @click="openProductDetail"
          >
            {{ board.productName }}
          </button>
          <span v-if="board.branchName"> · {{ board.branchName }}</span>
        </div>
        <div class="text-body-2 text-pre-wrap" style="white-space: pre-wrap">{{ board.boardText || '-' }}</div>
      </v-card>

      <v-card v-if="attachments.length" rounded="lg" elevation="1" class="mb-4 pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-3">첨부파일</div>
        <div class="d-flex flex-column gap-2">
          <a
            v-for="file in attachments"
            :key="file.fileCode"
            :href="file.fileUrl"
            target="_blank"
            rel="noopener"
            class="d-flex align-center justify-space-between rounded-lg border pa-3 text-decoration-none"
          >
            <span class="text-body-2 font-weight-medium text-primary">{{ file.originalFileName }}</span>
            <span class="text-caption text-grey">{{ formatFileSize(file.fileSize) }}</span>
          </a>
        </div>
      </v-card>

      <div class="text-subtitle-2 font-weight-bold mb-2">문의/답변</div>
      <v-card
        v-for="c in comments"
        :key="c.boardCommentCode"
        rounded="lg"
        elevation="1"
        class="mb-3 pa-3"
        :class="c.boardCommentType === 'ANSWER' ? 'inquiry-answer-card' : ''"
      >
        <div class="d-flex justify-space-between align-center mb-2">
          <v-chip size="small" :color="c.boardCommentType === 'ANSWER' ? 'primary' : 'secondary'" variant="tonal">
            {{ c.boardCommentType === 'ANSWER' ? '답변(관리자)' : '문의' }}
          </v-chip>
          <span class="text-caption text-grey">{{ formatDt(c.insertDt) }}</span>
        </div>
        <div class="text-body-2 text-grey mb-1">
          {{ c.boardCommentType === 'ANSWER' ? `관리자 ${c.userName || c.branchAdminCode || ''}` : (c.userName || c.userCode || '회원') }}
        </div>

        <template v-if="editingCode === c.boardCommentCode">
          <v-textarea v-model="editingText" variant="outlined" rows="4" class="mb-2" />
          <div class="d-flex gap-2">
            <v-btn size="small" variant="text" @click="cancelEdit">취소</v-btn>
            <v-btn size="small" color="primary" :loading="sending" @click="saveEdit">저장</v-btn>
          </div>
        </template>
        <template v-else>
          <div class="text-body-2 text-pre-wrap" style="white-space: pre-wrap">{{ c.boardCommentText || '-' }}</div>
          <div v-if="canEditAnswer(c)" class="d-flex gap-2 mt-2">
            <v-btn size="small" variant="tonal" @click="startEdit(c)">수정</v-btn>
            <v-btn size="small" color="error" variant="text" @click="removeAnswer(c)">삭제</v-btn>
          </div>
        </template>
      </v-card>

      <v-card rounded="lg" elevation="1" class="pa-4">
        <div class="text-subtitle-2 font-weight-bold mb-2">답변 작성</div>
        <v-textarea v-model="replyText" variant="outlined" rows="4" placeholder="답변 내용을 입력하세요." />
        <v-btn color="primary" class="mt-2" :loading="sending" :disabled="!replyText.trim()" @click="sendReply">
          답변 등록
        </v-btn>
      </v-card>
    </template>

    <v-alert v-else-if="!loading" type="info" variant="tonal">
      문의를 불러오지 못했거나 접근 권한이 없습니다.
    </v-alert>
  </div>
</template>

<style scoped>
.inquiry-linked-product {
  border: 1px solid rgba(148, 163, 184, 0.22);
  background: rgba(15, 23, 42, 0.52);
  color: #dbeafe;
}

.inquiry-linked-product strong {
  color: #f8fafc;
}

.inquiry-product-link {
  color: #f8fafc;
  cursor: pointer;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.inquiry-product-link:hover {
  color: #d8b768;
}

.inquiry-product-link:disabled {
  color: #f8fafc;
  cursor: default;
  text-decoration: none;
}

.inquiry-answer-card {
  border-color: rgba(216, 183, 104, 0.24) !important;
  background: rgba(216, 183, 104, 0.08) !important;
}

:deep(.v-card) {
  border: 1px solid rgba(148, 163, 184, 0.18);
}
</style>
